import React, { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, loginEmailPassword } from "../../services/firebaseAuth";
import isEmail from "validator/lib/isEmail";
import isStrongPassword from "validator/lib/isStrongPassword";
import { Button, Grid, Typography, useTheme } from "@mui/material";
import { FirebaseError } from "firebase/app";
import CustomTextField from "../Base/CustomTextField";
import BackgroundWrapper from "../BackgroundWrapper";
import { AppInfoBanner } from "../Base/AppInfoBanner";
import { AuthPageLink } from "../Base/AuthPageLink";
import { getRedirectUrl } from "../../utils/redirectUtils";

const Login: FC = () => {
  const navigate = useNavigate();
  const [signInSuccessMsg, setSignInSuccessMsg] = useState("");

  const [user, loading] = useAuthState(auth);

  const [email, setEmail] = useState<string>("");
  const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
  const [isEmailDirty, setIsEmailDirty] = useState<boolean>(false);

  const [password, setPassWord] = useState<string>("");
  const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false);
  const [isPasswordDirty, setIsPasswordDirty] = useState<boolean>(false);

  const handleEmailChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const emailValue: string = e.target.value;
    setEmail(emailValue);
    setIsEmailValid(isEmail(emailValue));
  };

  const handlePasswordChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const passwordValue: string = e.target.value;
    setPassWord(passwordValue);
    setIsPasswordValid(isStrongPassword(passwordValue));
  };

  useEffect(() => {
    if (loading) return;
    if (user) {
      // Rediriger vers l'URL sauvegardée ou la page d'accueil
      navigate(getRedirectUrl());
    }
  }, [user, loading, navigate]);

  const theme = useTheme();

  return (
    <BackgroundWrapper>
      <Grid
        container
        direction="column"
        rowSpacing={4}
        justifyContent="center"
        alignItems="center"
        sx={{
          maxWidth: "600px", // Largeur maximale
          width: "100%", // Prend toute la largeur disponible
          mx: "auto", // Centré horizontalement
        }}
      >
        <Grid item sx={{ width: "100%" }}>
          <AppInfoBanner>
            Afin de pouvoir vous inscrire au tournoi ou accéder à certaines
            parties du site, merci de vous connecter.
          </AppInfoBanner>
        </Grid>

        <Grid item sx={{ width: "100%" }}>
          <CustomTextField
            label="Email"
            type="email"
            autoFocus
            fullWidth
            required
            value={email}
            onChange={handleEmailChange}
            onBlur={() => setIsEmailDirty(true)}
            error={isEmailDirty && !isEmailValid}
            helperText={isEmailDirty && !isEmailValid && "Email invalide"}
          />
        </Grid>

        {/* Password Input */}
        <Grid item sx={{ width: "100%" }}>
          <CustomTextField
            label="Mot de passe"
            type="password"
            fullWidth
            required
            value={password}
            InputLabelProps={{
              shrink: true, // ← FORCÉ pour éviter le bug autofill
            }}
            onChange={handlePasswordChange}
            onBlur={() => setIsPasswordDirty(true)}
            error={isPasswordDirty && !isPasswordValid}
            helperText="Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial."
          />
        </Grid>

        <Grid item>
          {signInSuccessMsg.length > 0 && (
            <Grid item sx={{ mb: 2 }}>
              <Typography variant="body2" color="error">
                {signInSuccessMsg}
              </Typography>
            </Grid>
          )}

          <Grid item>
            <Button
              variant="contained"
              disabled={!isPasswordValid || !isEmailValid}
              onClick={async () => {
                try {
                  const result = await loginEmailPassword(email, password);
                  if (!result) {
                    setSignInSuccessMsg(
                      "Erreur lors de la connexion. Réessayez plus tard."
                    );
                  }
                } catch (error) {
                  if (
                    error instanceof FirebaseError &&
                    error.code === "auth/invalid-credential"
                  ) {
                    setSignInSuccessMsg(
                      "Combinaison email/mot de passe invalide."
                    );
                  } else {
                    setSignInSuccessMsg(
                      "Erreur lors de la connexion. Réessayez plus tard."
                    );
                  }
                }
              }}
              sx={{
                width: "100%",
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                mt: 2,
                backgroundColor: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                  transform: "scale(1.05)", // Agrandissement du bouton au survol
                  transition: "transform 0.3s ease-in-out", // Transition douce
                },
              }}
            >
              Se connecter
            </Button>
          </Grid>
        </Grid>

        {/* Links to signup and reset password */}
        <Grid item container justifyContent="space-between" sx={{ mt: 2 }}>
          <Grid item>
            <AuthPageLink to="/signup">Pas de compte ?</AuthPageLink>
          </Grid>
          <Grid item>
            <AuthPageLink to="/reset-password">
              Mot de passe oublié ?
            </AuthPageLink>
          </Grid>
        </Grid>
      </Grid>
    </BackgroundWrapper>
  );
};

export default Login;
